import React, { useState, useLayoutEffect, useContext, useRef } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import Button from '../components/button';
import PortableText from '../components/portable-text';
import ArrowRight from '../images/svg/arrow-right.svg'
import And from '../images/svg/and.svg';

export default function Footer({ setShowSubscribeModal }) {
  const [formStatus, setFormStatus] = useState('idle');
  const formLoadTime = useRef(Date.now());

  const handleSubscribeSubmit = async (e) => {
    e.preventDefault();

    if (Date.now() - formLoadTime.current < 2000) {
      return;
    }

    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const enquiryUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    
    const submitData = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      website: formData.get('website'),
      enquiryUrl: enquiryUrl,
    };

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const data = useStaticQuery(graphql`
    query {
      footer: sanityFooter(_id: {eq: "footer"}) {
        subscribeText
        addresses {
          _rawContent
        }
        instagramLink
        linkedInLink
      }
    }
  `);

  return (
    <footer
      css={css`
        display: flex;
        flex-wrap: wrap;
        position: relative;
        background-color: var(--white);
        padding: 0 var(--margin);
        border-top: 1px solid var(--black);

        @media (max-width: 900px) {
          padding: 0
        }
      `}
    >
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: var(--gutter);
          flex: 0 0 50%;
          padding: 8rem 0 18rem;
          padding-right: calc(var(--gutter) / 2);
          border-right: 1px solid var(--black);

          @media (max-width: 900px) {
            display: block;
            flex: 0 0 100%;
            padding: 5rem var(--margin);
            border-right: 0;
            border-bottom: 1px solid var(--black);
          }
        `}
      >
        <div
          css={css`
            grid-column: span 5;
          `}
        >
          <div
            className="type--large"
            css={css`
              margin-bottom: 1.5rem;
            `}
          >
            Stay in touch
          </div>
          <div
            css={css`
              width: 80%;
              margin-bottom: 6rem;
            `}
          >
            {data.footer?.subscribeText}
          </div>
          {formStatus === 'success' ? (
            <div className="font--medium" css={css`padding: 2rem 0; margin-bottom: 2rem;`}>Thank you for subscribing.</div>
          ) : (
          <form
            id="subscribe-form"
            onSubmit={handleSubscribeSubmit}
          >
            <input
              type="text"
              name="website"
              css={css`
                display: none;
              `}
              tabIndex="-1"
              autoComplete="off"
            />
            <fieldset disabled={formStatus === 'submitting'} css={css`border:0;padding:0;margin:0;`}>
            <div
              css={css`
                display: flex;
                gap: 0 2rem;

                @media (max-width: 800px) {
                  display: block;
                }
              `}
            >
              <input
                className="type--medium"
                css={css`
                  width: 100%;
                  font-family: 'Maax Medium', sans-serif;
                  background-color: transparent;
                  padding: 1.4rem 0;
                  border: 0;
                  border-bottom: 1px solid var(--black);

                  @media (max-width: 800px) {
                    margin-bottom: 1rem;
                  }

                  &::placeholder {
                    color: rgba(0, 0, 0, 0.3);
                  }

                  &:focus::placeholder {
                    color: transparent;
                  }
                `}
                type="text"
                name="firstName"
                placeholder="First Name"
                required
              />
              <input
                className="type--medium"
                css={css`
                  width: 100%;
                  font-family: 'Maax Medium', sans-serif;
                  background-color: transparent;
                  padding: 1.4rem 0;
                  border: 0;
                  border-bottom: 1px solid var(--black);

                  &::placeholder {
                    color: rgba(0, 0, 0, 0.3);
                  }

                  &:focus::placeholder {
                    color: transparent;
                  }
                `}
                type="text"
                name="lastName"
                placeholder="Last Name"
                required
              />
            </div>
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);
                margin-bottom: 1.2rem;

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="email"
              name="email"
              placeholder="Email address"
              required
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                margin-bottom: 4rem;
              `}
            >
              <input
                type="checkbox"
                id="terms-checkbox"
                required
                defaultChecked
                css={css`
                  margin-top: 0.3rem;
                  appearance: none;
                  width: 1.6rem;
                  height: 1.6rem;
                  border: 1px solid var(--black);
                  background-color: transparent;
                  cursor: pointer;
                  
                  &:checked {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 6l12 12M6 18L18 6' stroke='black' stroke-width='2' fill='none'/%3E%3C/svg%3E");
                    background-position: center;
                    background-repeat: no-repeat;
                    background-size: 1.2rem;
                  }
                `}
              />
              <label 
                htmlFor="terms-checkbox" 
                className="font--medium type--extra-extra-small"
                css={css`
                  position: relative;
                  top: 0.2rem;
                  cursor: pointer;

                  a {
                    &:hover {
                      text-decoration: none;
                    }
                  }
                `}
              >
                I accept the Terms &amp; Conditions of the Time &amp; Place <Link to="/privacy-policy/">Privacy Policy</Link>.
              </label>
            </div>
            <Button 
              submit="subscribe-form"
              text={formStatus === 'submitting' ? 'Signing Up...' : 'Sign Up'}
              arrow={<ArrowRight />}
            />
            </fieldset>
            {formStatus === 'error' && <div className="type--extra-extra-small" css={css`margin-top: 2rem;`}>There was an error submitting the form. Please try again.</div>}
          </form>
          )}
        </div>
      </div>
      <div
        className="type--mono"
        css={css`
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: var(--gutter);
          flex: 0 0 50%;
          padding: 9.5rem 0 8rem;
          padding-left: calc(var(--gutter) / 2);

          @media (max-width: 900px) {
            display: block;
            flex: 0 0 100%;
            padding: 5rem var(--margin);
            border-right: 0;
          }

          a {
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        `}
      >

        {data.footer?.addresses.length > 0 && data.footer?.addresses
          .map((address, i) => (
            <div
              key={i}
              css={css`
                grid-column-start: ${(i % 2 === 0) ? 2 : 5 };
                grid-column-end: span 3;
                margin-bottom: 8rem;

                &:last-of-type {
                  margin-bottom: 0;
                }
              `}
            >
              <PortableText blocks={address._rawContent} />
            </div>
          ))
        }
      </div>
      <div
        className="type--extra-small"
        css={css`
          position: absolute;
          bottom: var(--margin);
          left: var(--margin);
          font-family: 'Maax Medium', sans-serif;

          @media (max-width: 900px) {
            position: static;
            padding: 5rem var(--margin) var(--margin);
            border-right: 0;
          }

          
          ul {
            padding: 0;
            margin: 0;

            li {
              display: inline-block;
              list-style: none;
              margin-right: 3rem;

              @media (max-width: 900px) {
                display: block;
                margin-right: 0;
                margin-bottom: 2rem;
              }

              &:last-of-type {
                margin-right: 0;
              }
            }
          }

          a {
            color: var(--black);
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        `}
      >
        <ul>
          <li>
            <a target="_blank" href={data.footer?.instagramLink}>Instagram</a>
          </li>
          <li>
            <a target="_blank" href={data.footer?.linkedInLink}>LinkedIn</a>
          </li>
        </ul>
      </div>
      <nav
        className="type--extra-small"
        css={css`
          position: absolute;
          bottom: var(--margin);
          right: var(--margin);
          font-family: 'Maax Medium', sans-serif;

          @media (max-width: 900px) {
            position: static;
            padding: 5rem var(--margin) var(--margin);
            border-right: 0;
          }

          
          ul {
            padding: 0;
            margin: 0;

            li {
              display: inline-block;
              list-style: none;
              margin-right: 3rem;

              @media (max-width: 900px) {
                display: block;
                margin-right: 0;
                margin-bottom: 2rem;
              }

              &:last-of-type {
                margin-right: 0;
              }
            }
          }

          a {
            color: var(--black);
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        `}
      >
        <ul>
          <li>
            <Link to="/privacy-policy/">Privacy Policy</Link>
          </li>
          <li>
            Copyright {new Date().getFullYear()}
          </li>
        </ul>
      </nav>
      <div
        css={css`
          display: none;
          position: absolute;
          bottom: var(--margin);
          right: var(--margin);
          padding-bottom: 1.4rem;

          @media (max-width: 900px) {
            display: block;
          }
        `}
      >
        <And />
      </div>
    </footer>
  )
}
